import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, FileSpreadsheet, Check, AlertCircle, Sparkles } from 'lucide-react';
import { useApp } from '../context/AppContext';

interface CSVUploadProps {
  onClose: () => void;
}

const CSVUpload: React.FC<CSVUploadProps> = ({ onClose }) => {
  const { uploadCSV } = useApp();
  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploadState, setUploadState] = useState<'idle' | 'uploading' | 'processing' | 'success' | 'error'>('idle');
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = (file: File) => {
    if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
      setFile(file);
      setUploadState('idle');
    } else {
      setUploadState('error');
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploadState('uploading');
    
    // Simulate upload progress
    for (let i = 0; i <= 100; i += 10) {
      await new Promise(resolve => setTimeout(resolve, 100));
      setProgress(i);
    }

    setUploadState('processing');
    setProgress(0);

    // Simulate processing with progress
    const processingSteps = [
      'Parsing CSV structure...',
      'Validating employee data...',
      'Running engagement analysis...',
      'Generating DDI scores...',
      'Computing behavioral flags...',
      'Finalizing dashboard update...',
    ];

    for (let i = 0; i < processingSteps.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 400));
      setProgress(Math.round(((i + 1) / processingSteps.length) * 100));
    }

    await uploadCSV(file);
    setUploadState('success');

    setTimeout(() => {
      onClose();
    }, 1500);
  };

  return (
    <motion.div
      style={styles.overlay}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        style={styles.modal}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button style={styles.closeButton} onClick={onClose}>
          <X size={20} />
        </button>

        <div style={styles.header}>
          <div style={styles.iconWrapper}>
            <FileSpreadsheet size={28} color="#69277F" />
          </div>
          <h2 style={styles.title}>Upload Employee Data</h2>
          <p style={styles.subtitle}>
            Upload a CSV file with employee information to analyze engagement patterns
          </p>
        </div>

        <AnimatePresence mode="wait">
          {uploadState === 'idle' && (
            <motion.div
              key="upload"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <div
                style={{
                  ...styles.dropzone,
                  ...(dragActive ? styles.dropzoneActive : {}),
                  ...(file ? styles.dropzoneWithFile : {}),
                }}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  style={{ display: 'none' }}
                  onChange={(e) => e.target.files && handleFile(e.target.files[0])}
                />

                {file ? (
                  <div style={styles.fileInfo}>
                    <FileSpreadsheet size={32} color="#10B981" />
                    <span style={styles.fileName}>{file.name}</span>
                    <span style={styles.fileSize}>
                      {(file.size / 1024).toFixed(1)} KB
                    </span>
                  </div>
                ) : (
                  <>
                    <motion.div
                      style={styles.uploadIcon}
                      animate={{ y: [0, -8, 0] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <Upload size={40} color="#69277F" />
                    </motion.div>
                    <p style={styles.dropText}>
                      Drag and drop your CSV file here
                    </p>
                    <p style={styles.dropSubtext}>
                      or click to browse
                    </p>
                  </>
                )}
              </div>

              {file && (
                <motion.button
                  style={styles.uploadButton}
                  onClick={handleUpload}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Sparkles size={18} />
                  Analyze with Dynami AI
                </motion.button>
              )}
            </motion.div>
          )}

          {(uploadState === 'uploading' || uploadState === 'processing') && (
            <motion.div
              key="progress"
              style={styles.progressContainer}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <motion.div
                style={styles.processingIcon}
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              >
                <Sparkles size={40} color="#06BCCA" />
              </motion.div>
              <h3 style={styles.progressTitle}>
                {uploadState === 'uploading' ? 'Uploading...' : 'Analyzing Data...'}
              </h3>
              <p style={styles.progressSubtitle}>
                {uploadState === 'uploading'
                  ? 'Securely transferring your data'
                  : 'Running engagement analysis with Dynami AI'}
              </p>
              <div style={styles.progressBar}>
                <motion.div
                  style={styles.progressFill}
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
              <span style={styles.progressText}>{progress}%</span>
            </motion.div>
          )}

          {uploadState === 'success' && (
            <motion.div
              key="success"
              style={styles.successContainer}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                style={styles.successIcon}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200 }}
              >
                <Check size={32} color="#10B981" />
              </motion.div>
              <h3 style={styles.successTitle}>Analysis Complete!</h3>
              <p style={styles.successSubtitle}>
                Your dashboard has been updated with new insights
              </p>
            </motion.div>
          )}

          {uploadState === 'error' && (
            <motion.div
              key="error"
              style={styles.errorContainer}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <AlertCircle size={40} color="#EF4444" />
              <h3 style={styles.errorTitle}>Invalid File Format</h3>
              <p style={styles.errorSubtitle}>
                Please upload a valid CSV file
              </p>
              <button
                style={styles.retryButton}
                onClick={() => setUploadState('idle')}
              >
                Try Again
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <div style={styles.infoBox}>
          <h4 style={styles.infoTitle}>CSV Format Guidelines</h4>
          <ul style={styles.infoList}>
            <li>Include columns: name, email, department, role, start_date</li>
            <li>Optional: salary, manager, recent_activity_metrics</li>
            <li>Maximum 500 employee records per upload</li>
          </ul>
        </div>
      </motion.div>
    </motion.div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modal: {
    background: 'white',
    borderRadius: '24px',
    padding: '32px',
    maxWidth: '500px',
    width: '100%',
    position: 'relative',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },
  closeButton: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    background: '#F3F4F6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#6B7280',
    border: 'none',
    transition: 'all 0.2s ease',
  },
  header: {
    textAlign: 'center',
    marginBottom: '24px',
  },
  iconWrapper: {
    width: '60px',
    height: '60px',
    borderRadius: '16px',
    background: 'linear-gradient(135deg, rgba(105, 39, 127, 0.1) 0%, rgba(255, 104, 197, 0.1) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#1F2937',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#6B7280',
    lineHeight: 1.5,
  },
  dropzone: {
    border: '2px dashed #D1D5DB',
    borderRadius: '16px',
    padding: '40px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    background: '#FAFAFA',
  },
  dropzoneActive: {
    borderColor: '#06BCCA',
    background: 'rgba(6, 188, 202, 0.05)',
  },
  dropzoneWithFile: {
    borderColor: '#10B981',
    background: 'rgba(16, 185, 129, 0.05)',
  },
  uploadIcon: {
    marginBottom: '16px',
  },
  dropText: {
    fontSize: '16px',
    fontWeight: 500,
    color: '#374151',
    marginBottom: '4px',
  },
  dropSubtext: {
    fontSize: '14px',
    color: '#9CA3AF',
  },
  fileInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  fileName: {
    fontSize: '16px',
    fontWeight: 500,
    color: '#1F2937',
  },
  fileSize: {
    fontSize: '14px',
    color: '#6B7280',
  },
  uploadButton: {
    width: '100%',
    padding: '16px',
    marginTop: '16px',
    background: 'linear-gradient(135deg, #69277F 0%, #431652 100%)',
    color: 'white',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    cursor: 'pointer',
    border: 'none',
  },
  progressContainer: {
    textAlign: 'center',
    padding: '40px 0',
  },
  processingIcon: {
    marginBottom: '20px',
    display: 'inline-block',
  },
  progressTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#1F2937',
    marginBottom: '8px',
  },
  progressSubtitle: {
    fontSize: '14px',
    color: '#6B7280',
    marginBottom: '24px',
  },
  progressBar: {
    width: '100%',
    height: '8px',
    background: '#E5E7EB',
    borderRadius: '4px',
    overflow: 'hidden',
    marginBottom: '12px',
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #06BCCA 0%, #FF68C5 100%)',
    borderRadius: '4px',
  },
  progressText: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#69277F',
  },
  successContainer: {
    textAlign: 'center',
    padding: '40px 0',
  },
  successIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    background: 'rgba(16, 185, 129, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  },
  successTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#1F2937',
    marginBottom: '8px',
  },
  successSubtitle: {
    fontSize: '14px',
    color: '#6B7280',
  },
  errorContainer: {
    textAlign: 'center',
    padding: '40px 0',
  },
  errorTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#1F2937',
    marginTop: '16px',
    marginBottom: '8px',
  },
  errorSubtitle: {
    fontSize: '14px',
    color: '#6B7280',
    marginBottom: '24px',
  },
  retryButton: {
    padding: '12px 24px',
    background: '#F3F4F6',
    color: '#374151',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    border: 'none',
  },
  infoBox: {
    marginTop: '24px',
    padding: '16px',
    background: '#F9FAFB',
    borderRadius: '12px',
    border: '1px solid #E5E7EB',
  },
  infoTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '8px',
  },
  infoList: {
    fontSize: '12px',
    color: '#6B7280',
    paddingLeft: '16px',
    margin: 0,
    lineHeight: 1.8,
  },
};

export default CSVUpload;


